export const GA_MEASUREMENT_ID = "G-BV22YSEEYS";
const SITE_NAME = "Vanigam360";
const BUSINESS_MODEL = "aquatics_marketplace";

const isBrowser = typeof window !== "undefined";

const hasAnalytics = () => isBrowser && typeof window.gtag === "function";

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normaliseItem = (item = {}) => ({
  item_id:
    item._id ||
    item.id ||
    item.productId ||
    item.itemId ||
    item.item?._id ||
    "",
  item_name: item.name || item.item?.name || "Product",
  item_category: item.category || item.item?.category || "",
  item_brand: item.sellerName || item.sellerId?.storeName || item.item?.sellerName || "AquaRoots Market",
  item_variant: item.productType || item.item?.productType || "",
  price: toNumber(item.price || item.item?.price),
  quantity: Math.max(1, toNumber(item.qty || item.quantity || 1)),
});

const buildItemsPayload = (items = []) => items.map(normaliseItem).filter((item) => item.item_id || item.item_name);

const readLocalJson = (key) => {
  if (!isBrowser) return null;
  try {
    return JSON.parse(window.localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

const resolveUserRole = () => {
  if (!isBrowser) return "guest";
  if (window.localStorage.getItem("admin-token")) return "super_admin";
  if (window.localStorage.getItem("seller-token")) return "seller";
  if (readLocalJson("customer")?.token) return "customer";
  return "guest";
};

const getPathContext = (path = "") => {
  const pathname = String(path || "/").split("?")[0].split("#")[0] || "/";

  if (pathname === "/") {
    return { page_type: "home", page_group: "storefront", page_section: "catalog" };
  }
  if (pathname.startsWith("/product/")) {
    return { page_type: "product_detail", page_group: "storefront", page_section: "catalog" };
  }
  if (pathname === "/cart") {
    return { page_type: "cart", page_group: "storefront", page_section: "checkout" };
  }
  if (pathname === "/checkout") {
    return { page_type: "checkout", page_group: "storefront", page_section: "checkout" };
  }
  if (pathname === "/orders") {
    return { page_type: "orders", page_group: "customer_account", page_section: "account" };
  }
  if (pathname === "/customer-login") {
    return { page_type: "customer_login", page_group: "authentication", page_section: "account" };
  }
  if (pathname === "/customer-signup") {
    return { page_type: "customer_signup", page_group: "authentication", page_section: "account" };
  }
  if (pathname === "/forgot-password") {
    return { page_type: "forgot_password", page_group: "authentication", page_section: "account" };
  }
  if (pathname.startsWith("/seller/")) {
    return { page_type: "seller_portal", page_group: "seller_workspace", page_section: "operations" };
  }
  if (pathname.startsWith("/super-admin") || pathname.startsWith("/admin")) {
    return { page_type: "super_admin_portal", page_group: "admin_workspace", page_section: "operations" };
  }

  return { page_type: "content_page", page_group: "storefront", page_section: "content" };
};

const withBaseContext = (params = {}) => ({
  site_name: SITE_NAME,
  business_model: BUSINESS_MODEL,
  user_role: resolveUserRole(),
  site_hostname: isBrowser ? window.location.hostname : "",
  ...params,
});

export const trackPageView = (path, title = "") => {
  if (!hasAnalytics()) return;
  const pathContext = getPathContext(path);
  window.gtag("event", "page_view", {
    ...withBaseContext(pathContext),
    page_path: path,
    page_title: title || (isBrowser ? document.title : ""),
    page_location: isBrowser ? window.location.href : "",
  });
};

export const trackEvent = (eventName, params = {}) => {
  if (!hasAnalytics()) return;
  window.gtag("event", eventName, withBaseContext(params));
};

export const trackLogin = (method = "email") => {
  trackEvent("login", { method });
};

export const trackSignUp = (method = "email") => {
  trackEvent("sign_up", { method });
};

export const trackViewItem = (item) => {
  const items = buildItemsPayload([item]);
  trackEvent("view_item", {
    currency: "INR",
    value: toNumber(item?.price),
    items,
  });
};

export const trackAddToCart = (item, quantity = 1) => {
  const items = buildItemsPayload([{ ...item, qty: quantity }]);
  trackEvent("add_to_cart", {
    currency: "INR",
    value: toNumber(item?.price) * Math.max(1, toNumber(quantity || 1)),
    items,
  });
};

export const trackViewCart = (items = [], value = 0) => {
  trackEvent("view_cart", {
    currency: "INR",
    value: toNumber(value),
    items: buildItemsPayload(items),
  });
};

export const trackViewItemList = ({
  items = [],
  listName = "Storefront catalog",
  filterType = "all",
  category = "all",
  searchTerm = "",
} = {}) => {
  const payload = buildItemsPayload(items).slice(0, 25);
  trackEvent("view_item_list", {
    item_list_name: listName,
    item_list_id: `${filterType}:${category}:${searchTerm || "none"}`,
    visible_items_count: items.length,
    active_product_filter: filterType,
    active_category_filter: category,
    search_term: searchTerm || undefined,
    items: payload,
  });
};

export const trackBeginCheckout = ({
  items = [],
  value = 0,
  deliveryMode = "",
  promoCode = "",
  source = "",
} = {}) => {
  trackEvent("begin_checkout", {
    currency: "INR",
    value: toNumber(value),
    coupon: promoCode || undefined,
    checkout_source: source || undefined,
    delivery_mode: deliveryMode || undefined,
    items: buildItemsPayload(items),
  });
};

export const trackPurchase = ({
  orderId = "",
  paymentId = "",
  amount = 0,
  items = [],
  promoCode = "",
  deliveryMode = "",
  paymentMethod = "",
} = {}) => {
  trackEvent("purchase", {
    transaction_id: paymentId || orderId || `order-${Date.now()}`,
    value: toNumber(amount),
    currency: "INR",
    coupon: promoCode || undefined,
    delivery_mode: deliveryMode || undefined,
    payment_type: paymentMethod || undefined,
    items: buildItemsPayload(items),
  });
};

export const trackSearch = (searchTerm = "", context = "storefront") => {
  const term = String(searchTerm || "").trim();
  if (!term) return;
  trackEvent("catalog_search", {
    search_term: term,
    search_context: context,
  });
};

export const trackFilterUse = ({
  filterType = "",
  filterValue = "",
  context = "storefront",
} = {}) => {
  if (!filterType || !filterValue) return;
  trackEvent("catalog_filter_used", {
    filter_type: filterType,
    filter_value: filterValue,
    filter_context: context,
  });
};

export const trackSupportClick = ({
  channel = "",
  context = "",
  orderId = "",
} = {}) => {
  if (!channel) return;
  trackEvent("support_contact_clicked", {
    support_channel: channel,
    support_context: context || undefined,
    order_reference: orderId || undefined,
  });
};

export const trackCouponAttempt = ({
  code = "",
  context = "checkout",
  stage = "entered",
} = {}) => {
  const coupon = String(code || "").trim().toUpperCase();
  if (!coupon) return;
  trackEvent("coupon_code_interaction", {
    coupon,
    coupon_context: context,
    coupon_stage: stage,
  });
};

export const trackCheckoutFailure = ({
  step = "",
  message = "",
  deliveryMode = "",
  source = "",
} = {}) => {
  trackEvent("checkout_issue", {
    checkout_step: step || undefined,
    error_message: String(message || "").slice(0, 150) || undefined,
    delivery_mode: deliveryMode || undefined,
    checkout_source: source || undefined,
  });
};

export const trackDashboardUsage = ({
  role = "",
  section = "",
  path = "",
} = {}) => {
  if (!role) return;
  trackEvent("workspace_opened", {
    user_role: role,
    dashboard_section: section || undefined,
    dashboard_path: path || undefined,
  });
};
